import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput } from "@components/Forms";
import CustomTextarea from "@components/Forms/CustomTextarea";
import { DocumentInfoIcon, DownloadIcon, InformationIcon } from "@components/Icons";
import Breadcrumb from "@components/UI/Breadcrumb";
import CustomSelect from "@components/UI/CustomSelect";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import { Modal } from "@components/UI";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { CHARACTER_LENGTH, TOAST } from "@constants/index";
import { LICENSE_TYPE_OPTIONS_NO_TRANSLATION } from "@constants/openSource";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, uppercase } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const TEST_ID = "component-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const formatLicensePeriodRange = (period, t) => {
  const from = period?.versionFrom;
  const to = period?.versionTo;
  if (!from && to) {
    return t("open-source.license-period-range-below", { version: to });
  }
  if (from && !to) {
    return t("open-source.license-period-range-from-open", { version: from });
  }
  if (from && to) {
    return `${from} – ${to}`;
  }
  return t("open-source.license-period-unbounded");
};

const getObligationRankMeta = (rank, t, colors) => {
  if (rank == null || Number.isNaN(Number(rank))) {
    return null;
  }
  const value = Number(rank);
  // Genesis obligation_rank: 1 (lightest) → 4 (heaviest)
  switch (value) {
    case 1:
      return {
        label: t("open-source.license-period-rank-low"),
        className: colors.licensePeriodRankLow,
      };
    case 2:
      return {
        label: t("open-source.license-period-rank-medium"),
        className: colors.licensePeriodRankMedium,
      };
    case 3:
      return {
        label: t("open-source.license-period-rank-high"),
        className: colors.licensePeriodRankHigh,
      };
    case 4:
      return {
        label: t("open-source.license-period-rank-critical"),
        className: colors.licensePeriodRankCritical,
      };
    default:
      return {
        label: String(value),
        className: colors.licensePeriodConfidenceDefault,
      };
  }
};

const getConfidenceClassName = (confidence, colors) => {
  const normalized = String(confidence || "").toLowerCase();
  if (normalized === "confirmed" || normalized === "high") {
    return colors.licensePeriodConfidenceConfirmed;
  }
  if (normalized === "likely" || normalized === "medium") {
    return colors.licensePeriodConfidenceLikely;
  }
  if (normalized === "possible" || normalized === "low") {
    return colors.licensePeriodConfidencePossible;
  }
  return colors.licensePeriodConfidenceDefault;
};

const LicensePeriodEvidenceCell = ({ evidence, colors, onOpen, t }) => {
  if (!evidence) {
    return <span className={colors.textNeutralMuted}>-</span>;
  }

  return (
    <button
      type="button"
      className={`inline-flex ${colors.textLicensePeriodAccent} ${colors.hoverTextInfoAccent}`}
      onClick={() => onOpen(evidence)}
      aria-label={t("open-source.view-full-evidence")}
      title={t("open-source.view-full-evidence")}
    >
      <DocumentInfoIcon size={18} />
    </button>
  );
};

export default function ComponentDetail() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { componentSeq } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [componentData, setComponentData] = useState({});
  const [editingData, setEditingData] = useState({});
  const [evidenceModalText, setEvidenceModalText] = useState("");
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const navigate = useNavigate();

  // API mutations
  const getComponentData = useMutationData(
    `${queryKeys.getComponentById}/${componentSeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setComponentData(response);
          setEditingData(response);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/components" } });
        } else {
          showErrorToast(t("open-source.get-component-data-error"));
        }
        console.error("Error getting component data:", error);
      },
    },
  );

  const updateComponent = useMutationData(
    (data) => `${queryKeys.updateComponent}/${data?.componentSeq}`,
    { method: "PUT" },
    {
      onSuccess: async (response) => {
        if (typeof response !== "string") {
          setComponentData(response);
          setEditingData(response);
          setIsEditMode(false);
          await queryClient.invalidateQueries({
            queryKey: [queryKeys.getComponentLicensePeriods, componentSeq],
          });
          showSuccessToast(t("open-source.component-update-success"));
        }
      },
      onError: (error) => {
        if (error?.response?.status === 409) {
          showErrorToast(t("status.error"), t("open-source.component-update-error-conflict"));
          return;
        }
        console.error("Error updating component:", error);
        const errorMessage = error?.response?.data || error?.message || "";
        if (errorMessage.includes("Component already exists with the same name, version, and license identifier.")) {
          ToastConstant(TOAST.error, t("open-source.component-already-exists"), "");
        } else {
          ToastConstant(TOAST.error, t("open-source.component-update-error"), "");
        }
      },
    },
  );

  const {
    data: licensePeriods = [],
    isLoading: licensePeriodsLoading,
  } = useFetchData(
    [queryKeys.getComponentLicensePeriods, componentSeq],
    `${queryKeys.getComponentById}/${componentSeq}/license-periods`,
    { method: "GET" },
    undefined,
    { enabled: Boolean(componentSeq) },
  );

  const licensePeriodList = Array.isArray(licensePeriods) ? licensePeriods : [];
  const currentLicensePeriod = licensePeriodList.find((period) => period?.appliesToCurrentVersion);
  const hasLicensePeriodMatch = Boolean(currentLicensePeriod);
  const showLicensePeriodUndetermined =
    !licensePeriodsLoading && (licensePeriodList.length === 0 || !hasLicensePeriodMatch);

  // Effects
  useEffect(() => {
    if (componentSeq) {
      getComponentData.mutate();
    }
  }, [componentSeq]);

  // Handlers
  const handleEdit = () => {
    setIsEditMode(true);
    setEditingData({ ...componentData });
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditingData({ ...componentData });
  };

  const handleSave = () => {
    updateComponent.mutate({
      componentSeq,
      ...editingData,
    });
  };

  const handleInputChange = (field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getNormalizedUrl = (url) => {
    if (!url) return "";
    return /^(https?:)?\/\//i.test(url) ? url : `https://${url}`;
  };

  if (getComponentData.isPending) {
    return (
      <div data-testid={tid("loading")}>
        <LoadingSpinner data-testid={tid("loading-spinner")} />
      </div>
    );
  }

  return (
    <div className={`${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header */}
      <div className="mb-6" data-testid={tid("header")}>
        {/* Breadcrumb */}
        <Breadcrumb
          data-testid={tid("breadcrumb")}
          items={[
            { label: t("navbar.component-list"), href: "/components" },
            {
              label: t("navbar.component-details"),
              href: null,
              onClick: isEditMode ? () => setIsEditMode(false) : undefined,
            },
            ...(isEditMode ? [{ label: t("open-source.edit-component") }] : []),
          ]}
          className="mb-4"
        />

        {/* Title and Action Buttons */}
        <div className="flex items-center justify-between" data-testid={tid("title-row")}>
          <h1 className={`text-2xl font-bold truncate ${colors.textPrimary}`} data-testid={tid("title")}>
            {componentData?.componentName || "LOADING..."}
          </h1>

          <div className="flex items-center space-x-3" data-testid={tid("header-actions")}>
            {isEditMode ? (
              <>
                <OutlineButton data-testid={tid("cancel-button")} onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
                <PrimaryButton
                  data-testid={tid("save-button")}
                  onClick={handleSave}
                  disabled={
                    updateComponent.isPending ||
                    !editingData.componentName?.trim() ||
                    !editingData.componentVersion?.trim() ||
                    !editingData.licenseIdentifier?.trim()
                  }
                >
                  {t("common.save-button")}
                </PrimaryButton>
              </>
            ) : (
              <PrimaryButton data-testid={tid("edit-button")} onClick={handleEdit}>{t("open-source.edit-component-button")}</PrimaryButton>
            )}
          </div>
        </div>
      </div>

      {/* Component Information Header Card */}
      {!isEditMode && (
        <div className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary} p-6 mb-4`} data-testid={tid("meta-section")}>
          <div className="grid grid-cols-4 gap-6" data-testid={tid("meta-grid")}>
            <div data-testid={fieldContainerTestId("creator")}>
              <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("creator")}>
                {t("common.creator")}
              </label>
              <UserNameWithId name={componentData?.createrName} id={componentData?.createrId} layout="inline" data-testid={fieldValueTestId("creator")} />
            </div>

            <div data-testid={fieldContainerTestId("create-at")}>
              <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("create-at")}>
                {t("common.create-at")}
              </label>
              <div className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("create-at")}>
                {formatDateTime(componentData?.createdAt) || "N/A"}
              </div>
            </div>

            <div data-testid={fieldContainerTestId("last-modified")}>
              <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("last-modified")}>
                {t("common.last-modified-date")}
              </label>
              <div className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("last-modified")}>
                {formatDateTime(componentData?.updatedAt) || "N/A"}
              </div>
            </div>

            <div data-testid={fieldContainerTestId("last-modified-by")}>
              <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("last-modified-by")}>
                {t("common.last-modified-by")}
              </label>
              <UserNameWithId name={componentData?.updaterName} id={componentData?.updaterId} layout="inline" data-testid={fieldValueTestId("last-modified-by")} />
            </div>
          </div>
        </div>
      )}

      {/* Main Information Cards */}
      <div data-testid={tid("form")} className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary}`}>
        <div className="grid grid-cols-2">
          {/* Component Information Card */}
          <div className={`p-6 border-r border-b-0 border-t-0 border-l-0 border-solid ${colors.borderPrimary}`} data-testid={tid("component-info-section")}>
            <h2 className={`text-lg font-bold ${colors.textPrimary} mb-4`} data-testid={tid("component-info-title")}>
              {uppercase(t("open-source.component-information-title"))}
            </h2>

            <div className={`space-y-0 border-t border-dashed ${colors.borderPrimary} mt-4`} data-testid={tid("component-info-fields")}>
              <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("component-name")}>
                <div className="w-1/4">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("component-name")}>
                    {t("open-source.component-name")} {isEditMode && <span className={colors.textRequired}>*</span>}
                  </span>
                </div>
                <div className="w-3/4">
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("component-name")}
                      value={editingData.componentName || ""}
                      onChange={(e) => handleInputChange("componentName", e.target.value)}
                      placeholder={t("open-source.enter-component-name")}
                      size="md"
                      maxLength={CHARACTER_LENGTH.COMMENT}
                    />
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage} break-words`} data-testid={fieldValueTestId("component-name")}>
                      {componentData?.componentName || "N/A"}
                    </span>
                  )}
                </div>
              </div>

              <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("component-version")}>
                <div className="w-1/4">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("component-version")}>
                    {t("open-source.component-version")} {isEditMode && <span className={colors.textRequired}>*</span>}
                  </span>
                </div>
                <div className="w-3/4">
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("component-version")}
                      value={editingData.componentVersion || ""}
                      onChange={(e) => handleInputChange("componentVersion", e.target.value)}
                      placeholder={t("open-source.enter-component-version")}
                      size="md"
                      maxLength={CHARACTER_LENGTH.COMMENT}
                    />
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage} break-words`} data-testid={fieldValueTestId("component-version")}>
                      {componentData?.componentVersion || "N/A"}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex pt-4" data-testid={fieldContainerTestId("cpe")}>
                <div className="w-1/4">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} break-words`} data-testid={fieldLabelTestId("cpe")}>
                    {t("open-source.cpe-detail")}
                  </span>
                </div>
                <div className="w-3/4">
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("cpe")}
                      value={editingData.cpe || ""}
                      onChange={(e) => handleInputChange("cpe", e.target.value)}
                      placeholder={t("open-source.enter-cpe")}
                      size="md"
                      maxLength={CHARACTER_LENGTH.COMMENT}
                    />
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage} break-words`} data-testid={fieldValueTestId("cpe")}>
                      {componentData?.cpe || "N/A"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* License Information Card */}
          <div className="p-6" data-testid={tid("license-info-section")}>
            <h2 className={`text-lg font-bold ${colors.textPrimary} mb-4`} data-testid={tid("license-info-title")}>
              {uppercase(t("open-source.license-information-title"))}
            </h2>

            <div className={`space-y-0 border-t border-dashed ${colors.borderPrimary} mt-4`} data-testid={tid("license-info-fields")}>
              <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-identifier")}>
                <div className="w-1/4">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-identifier")}>
                    {t("license-policy.license-identifier")} {isEditMode && <span className={colors.textRequired}>*</span>}
                  </span>
                </div>
                <div className="w-3/4">
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("license-identifier")}
                      value={editingData.licenseIdentifier || ""}
                      onChange={(e) => handleInputChange("licenseIdentifier", e.target.value)}
                      placeholder={t("open-source.enter-license-identifier")}
                      size="md"
                      maxLength={CHARACTER_LENGTH.COMMENT}
                    />
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage} break-words`} data-testid={fieldValueTestId("license-identifier")}>
                      {componentData?.licenseIdentifier || "N/A"}
                    </span>
                  )}
                </div>
              </div>

              <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-name")}>
                <div className="w-1/4">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-name")}>
                    {t("license-policy.license-name")}
                  </span>
                </div>
                <div className="w-3/4">
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("license-name")}
                      value={editingData.licenseName || ""}
                      onChange={(e) => handleInputChange("licenseName", e.target.value)}
                      placeholder={t("open-source.enter-license-name")}
                      size="md"
                      maxLength={CHARACTER_LENGTH.COMMENT}
                    />
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage} break-words`} data-testid={fieldValueTestId("license-name")}>
                      {componentData?.licenseName || "N/A"}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex pt-4" data-testid={fieldContainerTestId("license-type")}>
                <div className="w-1/4">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-type")}>
                    {t("open-source.license-type")}
                  </span>
                </div>
                <div className="w-3/4">
                  {isEditMode ? (
                    <div className="relative z-10">
                      <CustomSelect
                        data-testid={fieldInputTestId("license-type")}
                        options={LICENSE_TYPE_OPTIONS_NO_TRANSLATION.map((opt) => ({
                          value: opt.value,
                          label: t(opt.label),
                        }))}
                        value={
                          editingData.licenseTypeCd || componentData?.licenseTypeCdName
                            ? {
                              value: editingData.licenseTypeCd || componentData?.licenseTypeCdName,
                              label: t(
                                LICENSE_TYPE_OPTIONS_NO_TRANSLATION.find(
                                  (o) => o.value === (editingData.licenseTypeCd || componentData?.licenseTypeCdName),
                                )?.label || "",
                              ),
                            }
                            : null
                        }
                        onChange={(opt) => handleInputChange("licenseTypeCd", opt?.value || "")}
                        placeholder={t("open-source.select-license-type")}
                        className="w-full"
                        menuPlacement="auto"
                        size="sm"
                      />
                    </div>
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("license-type")}>
                      {componentData?.licenseTypeCdName || "N/A"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Links Section */}
      <div className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary} p-6 mt-4`} data-testid={tid("links-section")}>
        <h2 className={`text-lg font-bold ${colors.textPrimary} mb-4`} data-testid={tid("links-title")}>{uppercase(t("open-source.links"))}</h2>

        <div className={`space-y-0 border-t border-dashed ${colors.borderPrimary} mt-4`} data-testid={tid("links-fields")}>
          <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("homepage-url")}>
            <div className="w-1/8">
              <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("homepage-url")}>
                {t("open-source.homepage-url")}
              </span>
            </div>
            <div className="w-5/8">
              {isEditMode ? (
                <CustomTextarea
                  data-testid={fieldInputTestId("homepage-url")}
                  value={editingData.homepageUrl || ""}
                  onChange={(e) => handleInputChange("homepageUrl", e.target.value)}
                  rows={3}
                  placeholder={t("open-source.homepage-url-placeholder")}
                  size="sm"
                  maxLength={CHARACTER_LENGTH.COMMENT}
                />
              ) : componentData?.homepageUrl ? (
                <a
                  href={getNormalizedUrl(componentData.homepageUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm font-medium ${colors.textComparisonLink} ${colors.hoverTextComparisonLink} underline-offset-2`}
                  data-testid={fieldValueTestId("homepage-url")}
                >
                  {componentData.homepageUrl}
                </a>
              ) : (
                <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("homepage-url")}>N/A</span>
              )}
            </div>
          </div>

          <div className="flex pt-4" data-testid={fieldContainerTestId("download-url")}>
            <div className="w-1/8">
              <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("download-url")}>
                {t("open-source.download")}
              </span>
            </div>
            <div className="w-6/8">
              {isEditMode ? (
                <CustomTextarea
                  data-testid={fieldInputTestId("download-url")}
                  value={editingData.downloadUrl || ""}
                  onChange={(e) => handleInputChange("downloadUrl", e.target.value)}
                  rows={3}
                  placeholder={t("open-source.download-url-placeholder")}
                  size="sm"
                  maxLength={CHARACTER_LENGTH.COMMENT}
                />
              ) : (
                <div className="flex items-center space-x-2">
                  <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("download-url")}>
                    {componentData?.downloadUrl || "N/A"}
                  </span>
                  {componentData?.downloadUrl ? (
                    <a
                      href={getNormalizedUrl(componentData.downloadUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className={`text-sm ${colors.textComparisonLink} ${colors.hoverTextComparisonLink} flex items-center px-2 py-1 rounded transition-colors`}
                      data-testid={tid("download-link")}
                    >
                      <DownloadIcon className="h-4 w-4 mr-1" />
                      {t("common.download")}
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className={`text-sm ${colors.textDisabled} flex items-center px-2 py-1 rounded cursor-not-allowed opacity-60`}
                      data-testid={tid("download-button-disabled")}
                    >
                      <DownloadIcon className="h-4 w-4 mr-1" />
                      {t("common.download")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Descriptive Notes Section */}
      <div className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary} p-6 mt-4`} data-testid={tid("notes-section")}>
        <h2 className={`text-lg font-bold ${colors.textPrimary} mb-4`} data-testid={tid("notes-title")}>
          {uppercase(t("open-source.descriptive-notes"))}
        </h2>

        <div className={`space-y-0 border-t border-dashed ${colors.borderPrimary} mt-4`} data-testid={tid("notes-fields")}>
          <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("copyright")}>
            <div className="w-24 flex-shrink-0">
              <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("copyright")}>
                {t("open-source.copyright")}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {isEditMode ? (
                <>
                  <CustomTextarea
                    data-testid={fieldInputTestId("copyright")}
                    value={editingData.copyright || ""}
                    onChange={(e) => handleInputChange("copyright", e.target.value)}
                    rows={4}
                    placeholder={t("open-source.copyright-placeholder")}
                    maxLength={CHARACTER_LENGTH.COMMENT}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("copyright-char-count")}>
                    {(editingData.copyright || "").length}/{CHARACTER_LENGTH.COMMENT}
                  </p>
                </>
              ) : (
                <CustomTextarea
                  data-testid={fieldValueTestId("copyright")}
                  rows={4}
                  value={componentData?.copyright || ""}
                  title={componentData?.copyright}
                  readOnly={true}
                />
              )}
            </div>
          </div>

          <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("description")}>
            <div className="w-24 flex-shrink-0">
              <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("description")}>
                {t("open-source.description-short")}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {isEditMode ? (
                <>
                  <CustomTextarea
                    data-testid={fieldInputTestId("description")}
                    value={editingData.description || ""}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    rows={4}
                    placeholder={t("open-source.description-placeholder")}
                    maxLength={CHARACTER_LENGTH.CONTENT}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("description-char-count")}>
                    {(editingData.description || "").length}/{CHARACTER_LENGTH.CONTENT}
                  </p>
                </>
              ) : (
                <CustomTextarea
                  data-testid={fieldValueTestId("description")}
                  rows={4}
                  value={componentData?.description || ""}
                  title={componentData?.description}
                  readOnly={true}
                />
              )}
            </div>
          </div>

          <div className="flex pt-4" data-testid={fieldContainerTestId("remark")}>
            <div className="w-24 flex-shrink-0">
              <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("remark")}>
                {t("license-policy.remark")}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {isEditMode ? (
                <>
                  <CustomTextarea
                    data-testid={fieldInputTestId("remark")}
                    value={editingData.remark || ""}
                    onChange={(e) => handleInputChange("remark", e.target.value)}
                    rows={4}
                    placeholder={t("open-source.remark-placeholder")}
                    maxLength={CHARACTER_LENGTH.REMARK}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("remark-char-count")}>
                    {(editingData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
                  </p>
                </>
              ) : (
                <div
                  className={`text-sm font-medium ${colors.textMessage} overflow-hidden`}
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                  }}
                  title={componentData?.remark || undefined}
                  data-testid={fieldValueTestId("remark")}
                >
                  <CustomTextarea
                    rows={4}
                    value={componentData?.remark || ""}
                    title={componentData?.remark}
                    readOnly={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isEditMode && (
        <div
          className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary} p-6 mt-4`}
          data-testid={tid("license-periods-section")}
        >
          <div className="flex items-start gap-3 mb-5" data-testid={tid("license-periods-header")}>
            <div className="min-w-0">
              <h2 className={`text-lg font-bold ${colors.textPrimary}`} data-testid={tid("license-periods-title")}>
                {uppercase(t("open-source.license-by-version-title"))}
              </h2>
              <p className={`text-sm mt-1 ${colors.textNeutralMuted}`} data-testid={tid("license-periods-description")}>
                {t("open-source.license-by-version-description")}
              </p>
            </div>
          </div>

          {licensePeriodsLoading ? (
            <div className="flex justify-center py-6" data-testid={tid("license-periods-loading")}>
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {hasLicensePeriodMatch && (
                <div
                  className={`mb-5 flex flex-wrap items-center gap-3 rounded-lg border ${colors.licensePeriodSummaryBorder} ${colors.bgSecondary} px-4 py-3`}
                  data-testid={tid("license-periods-summary")}
                >
                  <InformationIcon size={18} className="flex-shrink-0" />
                  <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-sm ${colors.textMessage}`}>
                    <span>
                      {t("open-source.license-period-current-version")}:{" "}
                      <span className={`font-semibold ${colors.textPrimary}`}>
                        {componentData?.componentVersion || "-"}
                      </span>
                    </span>
                    <span className={`hidden sm:inline ${colors.textNeutralMuted}`}>|</span>
                    <span>
                      {t("open-source.license-period-applies-to")}:{" "}
                      <span className={`font-semibold ${colors.textPrimary}`}>
                        {formatLicensePeriodRange(currentLicensePeriod, t)}
                      </span>
                    </span>
                  </div>
                  <span
                    className={`ml-auto inline-flex max-w-full truncate rounded-full px-3 py-1 text-xs font-medium ${colors.licensePeriodSummaryChip}`}
                    title={
                      currentLicensePeriod?.licenseName ||
                      currentLicensePeriod?.licenseIdentifier ||
                      undefined
                    }
                  >
                    {currentLicensePeriod?.licenseName ||
                      currentLicensePeriod?.licenseIdentifier ||
                      "-"}
                  </span>
                </div>
              )}

              {showLicensePeriodUndetermined && (
                <p className={`text-sm ${colors.textNeutralMuted} mb-4`} data-testid={tid("license-periods-undetermined")}>
                  {licensePeriodList.length === 0
                    ? t("open-source.license-period-empty")
                    : t("open-source.license-period-undetermined")}
                </p>
              )}

              {licensePeriodList.length > 0 && (
                <div className="overflow-x-auto" data-testid={tid("license-periods-table")}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${colors.borderPrimary}`}>
                        <th className={`text-left py-3 pr-3 font-medium ${colors.textSubTitle}`}>
                          {t("open-source.license-period-version-range")}
                        </th>
                        <th className={`text-left py-3 pr-3 font-medium ${colors.textSubTitle}`}>
                          {t("open-source.license-period-license")}
                        </th>
                        <th className={`text-left py-3 pr-3 font-medium ${colors.textSubTitle}`}>
                          {t("open-source.license-period-obligation-rank")}
                        </th>
                        <th className={`text-left py-3 pr-3 font-medium ${colors.textSubTitle}`}>
                          {t("open-source.license-period-evidence")}
                        </th>
                        <th className={`text-left py-3 font-medium ${colors.textSubTitle}`}>
                          {t("open-source.license-period-confidence")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {licensePeriodList.map((period) => {
                        const applies = Boolean(period?.appliesToCurrentVersion);
                        const obligationMeta = getObligationRankMeta(period?.obligationRank, t, colors);
                        const licenseName = period?.licenseName;
                        const licenseIdentifier = period?.licenseIdentifier;
                        return (
                          <tr
                            key={period?.componentLicensePeriodSeq || period?.id}
                            className={`border-b ${colors.borderPrimary} ${applies ? colors.bgSecondary : ""
                              }`}
                          >
                            <td
                              className={`py-3.5 pr-3 align-middle ${colors.textMessage} ${applies ? `${colors.licensePeriodCurrentAccent} pl-3` : ""
                                }`}
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                {applies && (
                                  <span
                                    className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${colors.licensePeriodCurrentBadge}`}
                                  >
                                    {t("open-source.license-period-current-badge")}
                                  </span>
                                )}
                                <span className={applies ? `font-semibold ${colors.textPrimary}` : ""}>
                                  {formatLicensePeriodRange(period, t)}
                                </span>
                              </div>
                            </td>
                            <td className={`py-3.5 pr-3 align-middle ${colors.textMessage}`}>
                              <div className={applies ? `font-medium ${colors.textPrimary}` : ""}>
                                {licenseName || licenseIdentifier || "-"}
                              </div>
                              {licenseName && licenseIdentifier && (
                                <div className={`text-xs mt-0.5 ${colors.textNeutralMuted}`}>
                                  ({licenseIdentifier})
                                </div>
                              )}
                            </td>
                            <td className="py-3.5 pr-3 align-middle">
                              {obligationMeta ? (
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${obligationMeta.className}`}
                                >
                                  {obligationMeta.label}
                                </span>
                              ) : (
                                <span className={colors.textNeutralMuted}>-</span>
                              )}
                            </td>
                            <td className="py-3.5 pr-3 align-middle">
                              <LicensePeriodEvidenceCell
                                evidence={period?.evidence}
                                t={t}
                                colors={colors}
                                onOpen={(text) => {
                                  setEvidenceModalText(text);
                                  setIsEvidenceModalOpen(true);
                                }}
                              />
                            </td>
                            <td className="py-3.5 align-middle">
                              {period?.confidence ? (
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${getConfidenceClassName(
                                    period.confidence,
                                    colors,
                                  )}`}
                                >
                                  {period.confidence}
                                </span>
                              ) : (
                                <span className={colors.textNeutralMuted}>-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Modal
        isOpen={isEvidenceModalOpen}
        onClose={() => setIsEvidenceModalOpen(false)}
        title={t("open-source.license-period-evidence")}
        size="md"
        data-testid={tid("evidence-modal")}
      >
        <div
          className={`p-6 text-sm whitespace-pre-wrap break-words ${colors.textMessage}`}
        >
          {evidenceModalText}
        </div>
      </Modal>
    </div>
  );
}
